import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Card, Typography, App, Alert, Tag, Space } from 'antd';
import { LockOutlined, UserOutlined, LoginOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/api';
import { useAdminAuthStore } from '../../stores/adminAuthStore';
import type { AdminUser, AdminRole, AdminCompanyScope } from '../../stores/adminAuthStore';
import { adminPath } from '../../lib/adminPaths';

const { Title, Text } = Typography;

type AdminLoginResponse = {
  token: string;
  admin: AdminUser;
};

const roleLabelMap: Record<AdminRole, string> = {
  system_admin: 'システム管理者',
  company_admin: '会社管理者',
  customer_success: 'カスタマーサクセス',
  finance: '財務担当',
  agency_manager: '代理店担当',
};

const scopeLabelMap: Record<AdminCompanyScope, string> = {
  all: '全社',
  company: '自社',
  assigned: '担当範囲',
};

export function AdminLoginPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const login = useAdminAuthStore((state) => state.login);

  // クライアントIPアドレスを取得
  const { data: clientIpData } = trpc.admin.getClientIp.useQuery();

  const loginMutation = trpc.admin.login.useMutation({
    onSuccess: (response: AdminLoginResponse) => {
      // 管理者ストアに保存
      login(response.admin, response.token);

      // localStorageにも保存（E2Eテスト用）
      localStorage.setItem('adminToken', response.token);

      message.success(
        `ログインに成功しました（権限: ${roleLabelMap[response.admin.role]} / スコープ: ${scopeLabelMap[response.admin.companyScope]}）`
      );
      navigate(adminPath('dashboard'));
    },
    onError: (err: unknown) => {
      const messageText = err instanceof Error ? err.message : 'ログインに失敗しました';
      setError(messageText);
      message.error('ログインに失敗しました');
    },
  });

  const handleLogin = (values: { email: string; password: string }) => {
    setError(null);
    loginMutation.mutate(values);
  };

  return (
    <div style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #2C3E50 0%, #3498DB 100%)',
      }}>
        <Card
          style={{
            width: 400,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <Title level={2} style={{ marginBottom: 8 }}>
              一元帳 管理画面
            </Title>
            <Text type="secondary">
              システム管理者ログイン
            </Text>
            {clientIpData && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <EnvironmentOutlined style={{ color: '#1890ff' }} />
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    接続元IP:
                  </Text>
                  <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                    {clientIpData.ip}
                  </Tag>
                </Space>
              </div>
            )}
          </div>

          {error && (
            <Alert
              message={error}
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              closable
              onClose={() => setError(null)}
            />
          )}

          <Form
            name="admin-login"
            onFinish={handleLogin}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              label="管理者メールアドレス"
              rules={[
                { required: true, message: 'メールアドレスを入力してください' },
                { type: 'email', message: '有効なメールアドレスを入力してください' },
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="admin@avs1866.com"
                size="large"
                autoComplete="email"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="パスワード"
              rules={[{ required: true, message: 'パスワードを入力してください' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="パスワード"
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loginMutation.isPending}
                icon={<LoginOutlined />}
              >
                ログイン
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button
              type="link"
              onClick={() => navigate('/login')}
            >
              一般ユーザーログインへ
            </Button>
          </div>

          <Alert
            message="セキュリティ通知"
            description="このページは管理権限を持つ許可済みIPのみアクセスできます。不正なアクセス試行は記録されます。"
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </Card>
      </div>
  );
}
